import { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { Download, Plus, TriangleAlert, Upload, X } from "lucide-react";
import { useScriptsApi } from "../api/scriptsApi";
import { useTags } from "../hooks/useTags";
import { useUsers } from "../hooks/useUsers";
import { useFunnels } from "../hooks/useFunnels";
import ScriptFlowCanvas from "../components/scripts/ScriptFlowCanvas";
import NodeConfigPanel from "../components/scripts/NodeConfigPanel";
import { removeNode, withPositions } from "../components/scripts/flowGraph";

const GROUP_LABEL = {
  conversa: "Conversa",
  logica: "Lógica",
  integracao: "Integrações",
  crm: "CRM",
};

const EMPTY = {
  name: "",
  description: "",
  enabled: true,
  keywords: [],
  priority: 0,
  respectBusinessHours: false,
};

const newId = () => `n${Math.random().toString(36).slice(2, 9)}`;

// Roteiro no formato linear antigo vira caixas de mensagem encadeadas, para
// nada se perder quando ele é aberto no editor visual.
const stepsToFlow = (steps = []) => {
  const nodes = steps.map((step, index) => ({
    id: newId(),
    type: "message",
    label: step.title,
    text: step.message,
    position: { x: 120, y: 60 + index * 140 },
  }));

  nodes.forEach((node, index) => {
    node.next = nodes[index + 1]?.id || null;
  });

  return { startNodeId: nodes[0]?.id || null, nodes };
};

const ScriptModal = ({ isOpen, onClose, onSaved, script }) => {
  const api = useScriptsApi();
  const { tags } = useTags();
  const { users } = useUsers();
  const { funnels } = useFunnels();
  const fileRef = useRef(null);

  const [form, setForm] = useState(EMPTY);
  const [flow, setFlow] = useState({ startNodeId: null, nodes: [] });
  const [catalog, setCatalog] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    api.catalog().then(setCatalog).catch(() => setCatalog(null));
  }, [isOpen, api]);

  useEffect(() => {
    if (!isOpen) return;

    setForm(script ? { ...EMPTY, ...script } : EMPTY);
    setFlow(
      script?.flow?.nodes?.length
        ? { ...script.flow, nodes: withPositions(script.flow.nodes) }
        : stepsToFlow(script?.steps || []),
    );
    setSelectedId(null);
    setWarnings([]);
  }, [script, isOpen]);

  const selected = useMemo(
    () => flow.nodes.find((node) => node.id === selectedId) || null,
    [flow, selectedId],
  );

  if (!isOpen || !catalog) return null;

  const stages = [...new Set(funnels.flatMap((funnel) => funnel.steps || []))];
  const field =
    "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100";

  const groups = Object.entries(catalog.nodes).reduce((accumulator, [type, spec]) => {
    accumulator[spec.group] = [...(accumulator[spec.group] || []), { type, spec }];
    return accumulator;
  }, {});

  const addNode = (type) => {
    const id = newId();
    // Entra ao lado da última caixa, e não sobre ela: caixa nova escondida
    // atrás de outra é o jeito mais rápido de parecer que o clique não fez nada.
    const last = flow.nodes[flow.nodes.length - 1];
    const position = last
      ? { x: last.position.x + 40, y: last.position.y + 150 }
      : { x: 120, y: 80 };

    setFlow((prev) => ({
      startNodeId: prev.startNodeId || id,
      nodes: [
        ...prev.nodes,
        { id, type, label: catalog.nodes[type].label, next: null, position },
      ],
    }));
    setSelectedId(id);
  };

  const patchSelected = (changes) =>
    setFlow((prev) => ({
      ...prev,
      nodes: prev.nodes.map((node) =>
        node.id === selectedId ? { ...node, ...changes } : node,
      ),
    }));

  const checkCompliance = async () => {
    const texts = flow.nodes
      .map((node) => [node.text, node.caption].filter(Boolean).join("\n"))
      .filter(Boolean)
      .join("\n\n");

    try {
      const { warnings: found } = await api.checkText(texts);
      setWarnings(found);
      toast[found.length ? "error" : "success"](
        found.length
          ? `${found.length} aviso(s) de conformidade.`
          : "Nenhum aviso de conformidade.",
      );
    } catch (error) {
      toast.error(error.message || "Não foi possível verificar.");
    }
  };

  const handleExport = () => {
    const blob = new Blob(
      [JSON.stringify({ formato: "whatsapp-crm/roteiro", versao: 1, ...form, flow }, null, 2)],
      { type: "application/json" },
    );

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `roteiro-${(form.name || "sem-nome").toLowerCase().replace(/\s+/g, "-")}.json`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const handleImport = async (file) => {
    if (!file) return;
    try {
      const payload = JSON.parse(await file.text());
      setForm({ ...EMPTY, ...payload, enabled: false });
      setFlow(
        payload.flow?.nodes?.length
          ? { ...payload.flow, nodes: withPositions(payload.flow.nodes) }
          : stepsToFlow(payload.steps || []),
      );
      setSelectedId(null);
      toast.success("Roteiro carregado. Revise e salve.");
    } catch {
      toast.error("Arquivo inválido: não é um JSON de roteiro.");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, flow, steps: [] };
      if (script) await api.update(script.id, payload);
      else await api.create(payload);

      toast.success("Roteiro salvo.");
      onSaved();
      onClose();
    } catch (error) {
      toast.error(error.message || "Não foi possível salvar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3">
      <div className="flex h-[94vh] w-full max-w-7xl flex-col rounded-xl bg-white shadow-xl dark:bg-gray-900">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 p-3 dark:border-gray-700">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            <input
              className={`${field} max-w-[14rem]`}
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              placeholder="Área (ex.: Trabalhista)"
            />
            <input
              className={`${field} max-w-[22rem]`}
              value={(form.keywords || []).join(", ")}
              onChange={(event) =>
                setForm({
                  ...form,
                  keywords: event.target.value.split(",").map((item) => item.trim()),
                })
              }
              placeholder="Palavras que acionam: demissão, rescisão, fgts"
            />

            {[
              ["enabled", "Ativo"],
              ["respectBusinessHours", "Só no expediente"],
            ].map(([key, label]) => (
              <label key={key} className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-green-500"
                  checked={Boolean(form[key])}
                  onChange={(event) => setForm({ ...form, [key]: event.target.checked })}
                />
                {label}
              </label>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={checkCompliance}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 dark:border-gray-600 dark:text-gray-300"
            >
              Conformidade
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="application/json"
              className="hidden"
              onChange={(event) => handleImport(event.target.files?.[0])}
            />
            <button
              onClick={() => fileRef.current?.click()}
              className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 dark:border-gray-600 dark:text-gray-300"
            >
              <Upload className="h-4 w-4" />
              Importar
            </button>
            <button
              onClick={handleExport}
              className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 dark:border-gray-600 dark:text-gray-300"
            >
              <Download className="h-4 w-4" />
              Exportar
            </button>
            <button onClick={onClose} className="rounded p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {warnings.length > 0 && (
          <div className="border-b border-amber-300 bg-amber-50 px-3 py-2 dark:border-amber-800 dark:bg-amber-900/20">
            {warnings.map((warning) => (
              <p key={warning.id} className="flex items-start gap-1 text-xs text-amber-800 dark:text-amber-200">
                <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  <strong>{warning.label}</strong> (“{warning.match}”) — {warning.explanation}
                </span>
              </p>
            ))}
          </div>
        )}

        <div className="flex min-h-0 flex-1">
          <aside className="w-48 shrink-0 overflow-y-auto border-r border-gray-200 p-2 dark:border-gray-700">
            <p className="mb-2 text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
              Caixas
            </p>
            {Object.entries(groups).map(([group, items]) => (
              <div key={group} className="mb-3">
                <p className="mb-1 text-[11px] font-medium text-gray-400">
                  {GROUP_LABEL[group] || group}
                </p>
                <div className="space-y-1">
                  {items.map(({ type, spec }) => (
                    <button
                      key={type}
                      onClick={() => addNode(type)}
                      className="flex w-full items-center gap-1.5 rounded-lg border border-gray-200 px-2 py-1.5 text-left text-xs text-gray-700 transition-colors hover:border-green-400 hover:bg-green-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                    >
                      <Plus className="h-3 w-3 shrink-0 text-gray-400" />
                      {spec.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </aside>

          <div className="min-w-0 flex-1 bg-gray-50 dark:bg-gray-800/40">
            {flow.nodes.length === 0 ? (
              <div className="flex h-full items-center justify-center p-10 text-center">
                <p className="max-w-sm text-sm text-gray-600 dark:text-gray-400">
                  Adicione a primeira caixa pela lista à esquerda. Depois, arraste
                  do ponto inferior de uma caixa até outra para ligá-las.
                </p>
              </div>
            ) : (
              <ScriptFlowCanvas
                flow={flow}
                catalog={catalog}
                selectedId={selectedId}
                onSelect={setSelectedId}
                onChange={setFlow}
              />
            )}
          </div>

          <aside className="w-80 shrink-0 overflow-hidden border-l border-gray-200 dark:border-gray-700">
            <NodeConfigPanel
              node={selected}
              spec={selected ? catalog.nodes[selected.type] : null}
              operators={catalog.operators}
              isStart={flow.startNodeId === selectedId}
              tags={tags}
              users={users}
              stages={stages}
              onChange={patchSelected}
              onRemove={() => {
                setFlow((prev) => removeNode(prev, selectedId));
                setSelectedId(null);
              }}
              onSetStart={() => setFlow((prev) => ({ ...prev, startNodeId: selectedId }))}
            />
          </aside>
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-gray-200 p-3 dark:border-gray-700">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {flow.nodes.length} caixa(s) · variáveis: {"{{primeiro_nome}}"} ·{" "}
            {"{{nome}}"} · {"{{telefone}}"} · {"{{ultima_mensagem}}"}
          </span>

          <div className="flex gap-2">
            <button onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 dark:border-gray-600 dark:text-gray-300">
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="rounded-lg bg-green-500 px-4 py-2 text-sm text-white hover:bg-green-600 disabled:opacity-60"
            >
              {saving ? "Salvando..." : "Salvar roteiro"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScriptModal;
